import { buildShow, type EventShow } from '../event-show';
import { DEFAULT_LOCALE, isSupportedLocale } from '../i18n/locales';
import { setPrefetched } from '../static-prefetch';
import { eventDetailColumns, supabase, type DbEvent } from '../supabase';
import type { SiteConfig } from '../site-config';
import { SITE } from '@site';

// Widened to the SiteConfig shape: eventSoldOut is absent from riviera's literal type.
const SITE_CONFIG: SiteConfig = SITE;

// The show pages' build-time data (/[lang]/events/<show_slug>), shared by the
// apps: every visible event row, grouped into shows. Fetched once per export
// process (not once per locale) — generateStaticParams is called for every
// cascaded {lang}, and events don't vary by locale. Riviera adds its old
// per-date redirect pages on top (its lib/detail/event-detail.ts).

/** Prefetch bucket for a show page's record ({ event, dates }), keyed by show_slug. */
export const EVENT_SHOW_BUCKET = 'event-show';

/**
 * The columns a show page reads: EVENT_COLUMNS, plus each date's sold-out flag
 * on a site whose feed has one (Platinumlist — `SITE.eventSoldOut`).
 */
export function showColumns(): string {
  return eventDetailColumns({ soldOut: !!SITE_CONFIG.eventSoldOut });
}

let showsPromise: Promise<Map<string, EventShow>> | null = null;

// PostgREST caps a response at 1000 rows (max_rows), and dubai has one row per
// date (~1400), so the rows are read in pages — ordered by id, a total order.
const ROWS_PAGE = 1000;

async function fetchVisibleRows(): Promise<DbEvent[]> {
  const rows: DbEvent[] = [];
  for (let from = 0; ; from += ROWS_PAGE) {
    const { data, error } = await supabase
      .from('events_visible')
      .select(showColumns())
      .order('id')
      .range(from, from + ROWS_PAGE - 1);
    if (error) throw new Error(`generateStaticParams(events): ${error.message}`);
    // A column list built at runtime: postgrest-js can't type the rows.
    const page = (data ?? []) as unknown as DbEvent[];
    rows.push(...page);
    if (page.length < ROWS_PAGE) return rows;
  }
}

/** Every show, by show_slug. Memoized: one read per export. */
export function fetchEventShows(): Promise<Map<string, EventShow>> {
  if (!showsPromise) {
    showsPromise = fetchVisibleRows().then((data) => {
      const bySlug = new Map<string, DbEvent[]>();
      for (const row of data) {
        const rows = bySlug.get(row.show_slug) ?? [];
        rows.push(row);
        bySlug.set(row.show_slug, rows);
      }
      const shows = new Map<string, EventShow>();
      for (const [slug, rows] of bySlug) shows.set(slug, buildShow(rows)!);
      return shows;
    });
  }
  return showsPromise;
}

// Enumerate every show so the static export emits one HTML file per
// {lang, show}, and prime the build cache so each file ships real <Head> meta
// + content. Argument shape: expo-router passes `{ params: parentParams }`,
// not the params directly — see the note in the app's article-detail.ts.
export async function generateEventStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; slug: string }[]> {
  const shows = await fetchEventShows();
  for (const [slug, show] of shows) setPrefetched(EVENT_SHOW_BUCKET, slug, show);
  // Fall back so a missing parent param can never silently produce a route
  // with an undefined locale segment.
  const lang = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  return [...shows.keys()].map((slug) => ({ lang, slug }));
}
